/**
 * SelectFieldType — pojedynczy wybór z listy opcji. Storage: PG TEXT.
 *
 * Opcje pochodzą z `field.options` (z `custom_module_field_options` — D9) ALBO,
 * gdy pole ma ustawiony `dictionaryCode` (Etap 6c — pole zainstalowane z szablonu
 * Kreatora wskazującego słownik globalny), ze słownika: wartość opcji = kod
 * pozycji słownika, etykieta formatowana wg `displayMode` słownika (patrz
 * `formatDictionaryOptionLabel`). Ten sam wzorzec co `ExtFieldValueInput`
 * (case 'select') — duck-typing, brak `dictionaryCode` = zachowanie bez zmian.
 *
 * Etap 10.3 (ADR-0066 §11) — gdy słownik pola ma niepuste `dictionaryParamBindings`
 * (parametry `record_field` słownika zewnętrznego wiążące się z polami-sąsiadami
 * TEGO SAMEGO modułu), opcje pochodzą z `useDictionaryOptions` — `POST
 * /api/dictionaries/:code/options` odpalane PRZY OTWARCIU dropdownu (natywny
 * `<select>` nie ma zdarzenia open/close, więc otwarcie aproksymujemy przez
 * focus/blur), z bieżącymi wartościami pól-sąsiadów czytanymi z `props.formData`
 * (`RecordFormPage.tsx` → `EditFieldRow` → `FieldRegistry.renderInput`). Bez
 * `dictionaryParamBindings` LUB bez rozwiązywalnego `dictionaryCode` (review fix,
 * Finding B — nic do pobrania bez celu) — zachowanie bez zmian: `useDictionary(code)`
 * (GET, kontekst firmy/usera rozwiązywany serwerowo, cache 2 min), bez żadnego
 * dodatkowego `onFocus`/`onBlur` na kontrolce.
 *
 * Wartość zapisana wcześniej, której pozycja słownikowa stała się nieaktywna/
 * ukryta, jest zawsze renderowana (dokładana jako opcja z surowym kodem jako
 * etykietą) — inaczej istniejące dane „znikałyby" z selecta.
 *
 * Etap 10.5 (ADR-0066 §11) — błąd materializacji słownika zewnętrznego (parametr
 * `record_field` brakujący/nieprawidłowy, albo awaria zapytania do źródła)
 * zwrócony przez `useDictionary`/`useDictionaryOptions` (`error`, patrz
 * `DictionaryQueryError`) jest renderowany jako czytelny komunikat pod selectem
 * (`mapDictionaryQueryError`) — dropdown pozostaje pusty (bez crasha), user
 * widzi PRZYCZYNĘ pustej listy zamiast domyślnego "brak opcji".
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import {
  formatDictionaryOptionLabel,
  hasParamBindings,
  mapDictionaryQueryError,
  useDictionary,
  useDictionaryOptions,
} from '@/hooks/useDictionary';

import type { FieldInputProps, FieldOption, FieldTypeDefinition } from '../FieldRegistry';

/** Dokłada opcję z surowym kodem jako etykietą, gdy zapisana wartość zniknęła z listy. */
function ensureOptionPresent(options: FieldOption[], value: string): FieldOption[] {
  if (!value || options.some((o) => o.value === value)) return options;
  return [...options, { value, label: value }];
}

// Uwaga (batch Dropdown): `Dropdown` nie przyjmuje `aria-invalid` — sygnalizacja błędu
// walidacji zostaje po stronie ramki callsite'a (tekst błędu); `props.error` nieużywane.
function SelectInput({ field, value, onChange, disabled, autoFocus, formData }: FieldInputProps) {
  const { t } = useTranslation();
  const dictionaryCode = field.dictionaryCode ?? '';

  const paramBindings = field.dictionaryParamBindings ?? null;
  const fieldHasParamBindings = hasParamBindings(paramBindings);
  // Finding B (review): bindings without a resolvable `dictionaryCode` have
  // nothing to fetch — gate the fetch-arm machinery (onFocus/onBlur below) on
  // BOTH, not on bindings alone, so a misconfigured field attaches no extra
  // focus tracking at all.
  const shouldArmOptionsFetch = fieldHasParamBindings && !!field.dictionaryCode;

  // `Dropdown` (Radix) nie eksponuje zdarzenia open/close — aproksymujemy "otwarcie
  // dropdownu" przez focus/blur na wrapperze (React deleguje focus/blur po drzewie
  // REACT, więc focus triggera ORAZ portalowanej listy opcji trzyma wrapper "w fokusie"
  // — semantyka identyczna z dawnym natywnym `<select>`: uzbrojony póki kontrolka ma
  // fokus). Stan (i handlery poniżej) mają znaczenie WYŁĄCZNIE dla ścieżki param-bound
  // — dla zwykłych selectów (bez `dictionaryParamBindings`) `isOpen` nigdy się nie
  // zmienia, więc nie płacimy za dodatkowy render przy każdym focus/blur.
  const [isOpen, setIsOpen] = useState(false);

  // `Dropdown` nie wspiera natywnego `autoFocus` — fokusujemy trigger przez ref.
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (autoFocus) triggerRef.current?.focus();
  }, [autoFocus]);

  // Oba hooki wywoływane bezwarunkowo (reguła React) — każdy sam gates fetch przez
  // pusty `code` (`useDictionary`: `enabled: !!code`; `useDictionaryOptions`:
  // `enabled: enabled && !!code`), więc tylko jedna ścieżka faktycznie odpytuje API.
  // Faza 2b (ADR-0068): RAW wiązania pola + wartości formularza idą do serwera —
  // rozwiązywanie wartości per parametr (w tym `context.*`) robi endpoint options.
  const detail = useDictionary(fieldHasParamBindings ? '' : dictionaryCode);
  const paramOptions = useDictionaryOptions(fieldHasParamBindings ? dictionaryCode : '', {
    bindings: paramBindings,
    recordValues: formData ?? {},
    enabled: isOpen,
  });
  const {
    dictionary,
    activeItems,
    error: dictionaryError,
  } = fieldHasParamBindings ? paramOptions : detail;
  const dictionaryErrorMessage = mapDictionaryQueryError(dictionaryError, t);

  const resolvedOptions: FieldOption[] = field.dictionaryCode
    ? dictionary
      ? activeItems.map((item) => ({
          value: item.code,
          label: formatDictionaryOptionLabel(
            item,
            field.dictionaryDisplayMode ?? dictionary.displayMode,
          ),
        }))
      : []
    : (field.options ?? []);

  const stringValue = typeof value === 'string' ? value : '';
  const options = ensureOptionPresent(resolvedOptions, stringValue);
  const dropdownEl = (
    <Dropdown
      ref={triggerRef}
      options={[{ value: '', label: '— wybierz —' }, ...options]}
      value={stringValue}
      onChange={(v) => onChange(v || null)}
      disabled={disabled}
      data-testid={`field-${field.slug}-input`}
    />
  );
  // Fetch-on-open (param-bound): wrapper z onFocus/onBlur — React propaguje focus/blur
  // po drzewie React (włącznie z portalem listy opcji), więc semantyka "uzbrojony póki
  // kontrolka sfokusowana" jest zachowana 1:1 z dawnym natywnym `<select>`.
  const selectEl = shouldArmOptionsFetch ? (
    <div onFocus={() => setIsOpen(true)} onBlur={() => setIsOpen(false)}>
      {dropdownEl}
    </div>
  ) : (
    dropdownEl
  );

  // Etap 10.5 — brak błędu: dokładnie ten sam pojedynczy element co przed
  // tym etapem (zero regresji dla injectowania `id`/`aria-*` przez `FormField`
  // przy cloneElement — patrz `FormField.tsx` — który omija tylko Fragmenty).
  if (!dictionaryErrorMessage) return selectEl;

  return (
    <>
      {selectEl}
      <p
        className="mt-1 text-xs text-red-600 dark:text-red-400"
        role="alert"
        data-testid={`field-${field.slug}-dictionary-error`}
      >
        {dictionaryErrorMessage}
      </p>
    </>
  );
}

export const SelectFieldType: FieldTypeDefinition = {
  type: 'select',
  renderInput: (props) => <SelectInput {...props} />,
  renderCell: (field, value) => {
    if (typeof value !== 'string' || !value) return '';
    // Render w komórce tabeli korzysta wyłącznie z `field.options` inline (bez
    // słownika — brak dostępu do hooka poza komponentem; zachowanie sprzed Etapu 6c
    // dla renderCell). Gdy pole ma dictionaryCode, brak dopasowania → surowa wartość.
    const opt = field.options?.find((o) => o.value === value);
    if (!opt) return value;
    return opt.color ? (
      <span
        className="inline-block rounded px-2 py-0.5 text-xs"
        style={{ backgroundColor: `${opt.color}20`, color: opt.color }}
      >
        {opt.label}
      </span>
    ) : (
      opt.label
    );
  },
  defaultValue: null,
};
